// @flow
import * as api from "../data/firebase-data";
import * as actionTypes from "../constants/action-types";

/** EXAMPLE ACTIONS

 ***ASYNC EXAMPLE:

 export const doSomeAction = (foo): (any => void) => {
    function thunk(dispatch) {
        someAsyncFunction(foo)
            .then((result: mixed) => {
                dispatch({ type: actionTypes.SOME_ACTION_SUCCESS, payload: {result}});
            })
            .catch((error: Error) => {
                dispatch({ type: actionTypes.SOME_ACTION_FAIL, payload: {error} });
            });
    }
    thunk.interceptOnOffline = true;
    return thunk;
};


 ***SYNCHRONOUS EXAMPLE:

 export const soSomeAction = (api: DataType): ActionType => (
 {
        type: actionTypes.SOME_ACTION,
        api
    }
 );

 **/

/**
 * Logs a user out
 * @returns {Promise} - logout
 */

export const logout = (): (any=>void) => {
    function thunk(dispatch) {
        api.logout()
            .then(() => {
                dispatch({ type: actionTypes.LOGOUT_SUCCESS });
            })
            .catch((error: Error) => {
                dispatch({ type: actionTypes.LOGOUT_FAIL, payload: { error: error } });
            });
    }

    thunk.interceptOnOffline = true;
    return thunk;
};

/**
 * Logs a user in
 * @param {string} email - email
 * @param {string} password - password
 * @returns {Promise} - auth results
 */
export const login = (email: string, password: string): (any=>void) => {
    function thunk(dispatch) {
        api.loginWithEmailPassword(email, password)
            .then(() => {
                dispatch({ type: actionTypes.LOGOUT_SUCCESS });
            })
            .catch((error: Error) => {
                dispatch({ type: actionTypes.LOGOUT_FAIL, payload: { error: error } });
            });
    }

    thunk.interceptOnOffline = true;
    return thunk;
};

/**
 * Clears a login error
 * @returns {void}
 */
export const clearLoginError = () => (
    { type: actionTypes.LOGIN_RESET }
);


export const initialize = (): (any => void) => {
    function thunk(dispatch) {
        const config = {
            userFailedAuthentication: error => dispatch({ type: actionTypes.LOGIN_FAIL, payload: { error } }),
            onAdminFetchFail: error => dispatch({ type: actionTypes.ADMINS_FETCH_FAIL, payload: { error } }),
            onAdminFetchSuccess: data => dispatch({ type: actionTypes.ADMINS_FETCH_SUCCESS, payload: { data } }),
            onEventFetchFail: error => dispatch({ type: actionTypes.FETCH_EVENT_INFO_FAIL, payload: { error } }),
            onEventFetchSuccess: data => dispatch({ type: actionTypes.FETCH_EVENT_INFO_SUCCESS, payload: { data } }),
            onFAQFetchFail: error => dispatch({ type: actionTypes.FETCH_FAQS_FAIL, payload: { error } }),
            onFAQFetchSuccess: data => dispatch({ type: actionTypes.FETCH_FAQS_SUCCESS, payload: { data } }),
            onLogin: data => dispatch({ type: actionTypes.LOGIN_SUCCESS, payload: { data } }),
            onLogout: () => dispatch({ type: actionTypes.LOGOUT_SUCCESS }),
            onMessageFetchFail: error => dispatch({ type: actionTypes.FETCH_MESSAGES_FAIL, payload: { error } }),
            onMessageFetchSuccess: data => dispatch({ type: actionTypes.FETCH_MESSAGES_SUCCESS, payload: { data } }),
            onPermissionFetchFail: error => dispatch({ type: actionTypes.FETCH_PERMISSIONS_FAIL, payload: { error } }),
            onPermissionFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_PERMISSIONS_SUCCESS,
                payload: { data }
            }),
            onTeamsFetchFail: error => dispatch({ type: actionTypes.FETCH_TEAMS_FAIL, payload: { error } }),
            onTeamFetchSuccess: data => dispatch({ type: actionTypes.FETCH_TEAMS_SUCCESS, payload: { data } }),
            onTeamMembersFetchFail: error => dispatch({
                type: actionTypes.FETCH_TEAM_MEMBERS_FAIL,
                payload: { error }
            }),
            onTeamMembersFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_TEAM_MEMBERS_SUCCESS,
                payload: { data }
            }),
            onTownsFetchFail: error => dispatch({ type: actionTypes.FETCH_TOWNS_FAIL, payload: { error } }),
            onTownsFetchSuccess: data => dispatch({ type: actionTypes.FETCH_TOWNS_SUCCESS, payload: { data } }),
            onTrashDropFetchFail: error => dispatch({
                type: actionTypes.FETCH_TRASH_DROPS_SUCCESS,
                payload: { error }
            }),
            onTrashDropFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_TRASH_DROPS_SUCCESS,
                payload: { data }
            }),
            onUsersFetchFail: error => dispatch({ type: actionTypes.FETCH_USERS_FAIL, payload: { error } }),
            onUsersFetchSuccess: data => dispatch({ type: actionTypes.FETCH_USERS_SUCCESS, payload: { data } }),
            onCelebrationsFetchFail: error => dispatch({
                type: actionTypes.FETCH_CELEBRATIONS_FAIL,
                payload: { error }
            }),
            onCelebrationsFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_CELEBRATIONS_SUCCESS,
                payload: { data }
            }),
            onSupplyDistributionSitesFetchFail: error => dispatch({
                type: actionTypes.FETCH_SUPPLY_DISTRIBUTION_SITES_FAIL,
                payload: { error }
            }),
            onSupplyDistributionSitesFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_SUPPLY_DISTRIBUTION_SITES_SUCCESS,
                payload: { data }
            }),
            onTrashCollectionSitesFetchFail: error => dispatch({
                type: actionTypes.FETCH_TRASH_COLLECTION_SITES_FAIL,
                payload: { error }
            }),
            onTrashCollectionSitesFetchSuccess: data => dispatch({
                type: actionTypes.FETCH_TRASH_COLLECTION_SITES_SUCCESS,
                payload: { data }
            })
        };
        api.initialize(config)
            .then(() => {
                dispatch({ type: actionTypes.INITIALIZE_SUCCESS });
            })
            .catch(error => {
                dispatch({ type: actionTypes.INITIALIZE_FAIL, error });
            });
    }

    thunk.interceptOnOffline = true;
    return thunk;
};
